import React, {useState} from "react";
import {forBrands, styled, useTheme, CSSObject} from "@core-ui/react-stitch";
import {
  PlayCircleIcon,
  PauseCircleIcon,
  PlaySquareIcon,
  PauseSquareIcon,
} from "@core-ui/icons";
import {
  AutoplayButtonProps,
  AutoplayStates,
  PlayPauseIconProps,
} from "../types";

const Button = styled.button(() => {
  const theme = useTheme();
  const dimensions = forBrands(theme, {
    on: {height: "54px", width: "50px"},
    default: {height: "48px", width: "48px"},
  }) as CSSObject;

  return {
    position: "absolute",
    border: "none",
    margin: "10px 8px",
    zIndex: 1,
    background: "transparent",
    bottom: 0,
    ...dimensions,
  };
});

const PauseIcon = ({
  colorTheme,
  iconSize,
  shape,
}: PlayPauseIconProps): JSX.Element => {
  if (shape === "square") {
    return <PauseSquareIcon colorTheme={colorTheme} size={iconSize} />;
  }
  return <PauseCircleIcon size={iconSize} />;
};

const PlayIcon = ({
  colorTheme,
  iconSize,
  shape,
}: PlayPauseIconProps): JSX.Element => {
  if (shape === "square") {
    return <PlaySquareIcon colorTheme={colorTheme} size={iconSize} />;
  }
  return <PlayCircleIcon size={iconSize} />;
};

const AutoplayButton = ({
  slickRef,
  colorTheme,
  customPlayPauseButton,
  pauseAltText,
  playAltText,
}: AutoplayButtonProps): JSX.Element => {
  const theme = useTheme();
  const getIconSize = forBrands(theme, {
    at: "44px",
    on: "50px",
    default: "2rem",
  }) as string;
  const iconSize = {width: getIconSize, height: getIconSize};
  const [autoplayBtnState, setAutoplayBtnState] =
    useState<AutoplayStates>("pause");
  const onClick = (): void => {
    if (autoplayBtnState === "pause") slickRef.current?.slickPause();
    else slickRef.current?.slickPlay();

    setAutoplayBtnState(autoplayBtnState === "pause" ? "play" : "pause");
  };

  const isAtOrOn = theme.brand === "at" || theme.brand === "on";

  if (customPlayPauseButton) {
    return React.cloneElement(customPlayPauseButton, {
      status: autoplayBtnState,
      colorTheme,
      onClick,
    });
  }

  if (autoplayBtnState === "pause") {
    return (
      <Button
        aria-label={pauseAltText}
        aria-pressed={false}
        data-testid="pause-button"
        onClick={onClick}
        tabIndex={0}
      >
        <PauseIcon
          colorTheme={colorTheme}
          iconSize={iconSize}
          shape={isAtOrOn ? "square" : "circle"}
        />
      </Button>
    );
  }

  return (
    <Button
      aria-label={playAltText}
      aria-pressed
      data-testid="play-button"
      onClick={onClick}
      tabIndex={0}
      type="button"
    >
      <PlayIcon
        colorTheme={colorTheme}
        iconSize={iconSize}
        shape={isAtOrOn ? "square" : "circle"}
      />
    </Button>
  );
};

export default AutoplayButton;
